<template>
  <!-- Footer row: opens the "About this project" modal and the Sentry feedback
       widget (which attaches to #feedback-button during idle time). -->
  <SectionColumn>
    <nav aria-label="More from Colorize" class="flex flex-wrap justify-center gap-2">
      <BaseButton href="/brand-colors" variant="soft" size="xs" @click="brandColorsClick">
        <template #icon>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-gray-400" aria-hidden="true">
            <path fill-rule="evenodd" d="M15.988 3.012A2.25 2.25 0 0 1 18 5.25v6.5A2.25 2.25 0 0 1 15.75 14H13.5v-3.379a3 3 0 0 0-.879-2.121l-3.12-3.121a3 3 0 0 0-1.007-.664V4.25A2.25 2.25 0 0 1 10.75 2h3A2.25 2.25 0 0 1 15.988 3.012ZM12 4.25a.75.75 0 0 1 .75-.75h.5a.75.75 0 0 1 0 1.5h-.5a.75.75 0 0 1-.75-.75Z" clip-rule="evenodd" />
            <path d="M4.25 6A2.25 2.25 0 0 0 2 8.25v7.5A2.25 2.25 0 0 0 4.25 18h5.5A2.25 2.25 0 0 0 12 15.75v-3.879a1.5 1.5 0 0 0-.44-1.06L7.94 7.19A1.5 1.5 0 0 0 6.88 6.75H4.25Z" />
          </svg>
        </template>
        Brand colors
      </BaseButton>
      <!-- Points at the extension's landing page, not straight at the Web
           Store: same reason as in SiteFooter. -->
      <BaseButton href="/browser-extension" variant="soft" size="xs" @click="extensionClick">
        <template #icon>
          <BrowserLogo browser="chrome" class="size-4 text-gray-400" />
        </template>
        Browser Extension
      </BaseButton>
      <BaseButton href="/blog" variant="soft" size="xs" @click="blogClick">
        <template #icon>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-4 text-gray-400" aria-hidden="true">
            <path d="M3 3.5A1.5 1.5 0 0 1 4.5 2h6.879a1.5 1.5 0 0 1 1.06.44l3.122 3.12A1.5 1.5 0 0 1 16 6.622V16.5a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 3 16.5v-13Zm3.25 5a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Zm0 3.5a.75.75 0 0 0 0 1.5h6.5a.75.75 0 0 0 0-1.5h-6.5Zm0-7a.75.75 0 0 0 0 1.5h3a.75.75 0 0 0 0-1.5h-3Z" />
          </svg>
        </template>
        Blog
      </BaseButton>
      <BaseButton variant="soft" size="xs" aria-haspopup="dialog" :aria-expanded="aboutOpen" @click="$emit('about')">
        <template #icon>
          <InformationCircleIcon class="size-4 text-gray-400" aria-hidden="true" />
        </template>
        About
      </BaseButton>
      <BaseButton id="feedback-button" variant="soft" size="xs" @click="feedbackClick">
        <template #icon>
          <ChatBubbleBottomCenterTextIcon class="size-4 text-gray-400" aria-hidden="true" />
        </template>
        Feedback
      </BaseButton>
    </nav>
  </SectionColumn>
</template>

<script setup>
import { InformationCircleIcon } from '@heroicons/vue/20/solid'
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/vue/16/solid'
import { trackEvent } from '~/composables/analytics'
import BaseButton from '~/components/atoms/BaseButton.vue'
import BrowserLogo from '~/components/atoms/BrowserLogo.vue'
import SectionColumn from '~/components/atoms/SectionColumn.vue'

defineProps({
  aboutOpen: { type: Boolean, default: false },
})
defineEmits(['about'])

// The two destinations that also sit in SiteFooter fire under the *same* event
// name here, so "Footer Link Clicked (browser extension)" is one number across
// the whole site rather than one per footer; `page` is what tells the two
// surfaces apart. See components/organisms/SiteFooter.vue.
const route = useRoute()

const trackFooterLink = (destination) => {
  try {
    trackEvent(`Footer Link Clicked (${destination})`, { page: route.path })
  } catch {}
}

// The remaining two buttons are actions with no counterpart in the other
// footer, so they keep their own names (and their existing history).
const feedbackClick = () => {
  try {
    trackEvent('Feedback Click', { page: route.path })
  } catch {}
}

const extensionClick = () => trackFooterLink('browser extension')

const blogClick = () => trackFooterLink('blog')

const brandColorsClick = () => trackFooterLink('brand colors')
</script>
